import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraRecordRail } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraRestrictedChip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { format } from 'date-fns';
import { useState } from 'react';
import { useProject } from '../../../hooks/useProjects';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { RfiEntitiesRailGroup, RfiEntityAddAction } from './RfiEntitiesRailGroup';
import { RfiSubmissionFactsDialog } from './RfiSubmissionFactsDialog';
import { useLinkRfiEntity } from './useRfiWorkspaceHome';
const T = Testids.rfiWorkspace;
function entityChip(e) {
    if (e.restricted) return /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
        label: "Restricted record"
    });
    if (!e.registryId) return /*#__PURE__*/ _jsxs(SpecraBadge, {
        variant: "warning",
        children: [
            e.pendingLabel || 'pending',
            " · confirm"
        ]
    });
    return /*#__PURE__*/ _jsx(SpecraLink, {
        href: e.href,
        renderLink: renderRouterLink,
        children: e.label
    });
}
/** "Add to this RFI" on one notice item (pins a match, or files the notice text as an observation). */ function NoticeAdd({ rfiId, noticeId, role, registryRef, observation }) {
    const link = useLinkRfiEntity(rfiId);
    return /*#__PURE__*/ _jsx(SpecraButton, {
        "data-testid": T.noticeAddToRfi,
        disabled: link.isPending,
        onClick: ()=>link.mutate({
                role,
                source: 'notice',
                sourceRef: noticeId,
                target: registryRef ? {
                    case: 'registryRef',
                    value: registryRef
                } : {
                    case: 'observation',
                    value: {
                        ...observation ?? {}
                    }
                }
            }),
        size: "inline",
        variant: "link",
        children: "Add to this RFI"
    });
}
function TeamRows({ zouProjectId, owned }) {
    const { data } = useProject(zouProjectId);
    const members = data?.members ?? [];
    if (members.length === 0) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            size: "meta",
            tone: "muted",
            children: "No members yet."
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "flex flex-col gap-[var(--space-1)]",
        children: members.map((m)=>/*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-ui",
                        children: m.name || m.email
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: [
                            m.role === 'owner' ? 'lead' : m.role,
                            owned.get(m.userId) ? ` · ${owned.get(m.userId)} owned` : ''
                        ]
                    })
                ]
            }, m.userId))
    });
}
/** The Home rail: Notice, Submission, People and organizations, Linked pursuits, Team. */ export function RfiHomeRail({ home, rfiId, zouProjectId }) {
    const [editing, setEditing] = useState(false);
    const n = home.notice;
    const byRole = (role)=>home.entities.filter((e)=>e.role === role);
    const sections = [];
    if (n?.noticeId) {
        const ref = (refId, refSource, text, role, obs)=>{
            const linked = home.entities.find((e)=>e.registryId === refId && e.role === role);
            if (refId && refSource === 'link' && linked) return entityChip(linked);
            if (refId && refSource === 'match') {
                return /*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex flex-wrap items-center gap-[var(--space-1-5)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "body-ui",
                            children: text
                        }),
                        home.canEdit ? /*#__PURE__*/ _jsx(NoticeAdd, {
                            noticeId: n.noticeId,
                            registryRef: {
                                kind: role === 'point_of_contact' ? 'CONTACT' : 'GOVERNMENT_ORG',
                                registryId: refId
                            },
                            rfiId: rfiId,
                            role: role
                        }) : null
                    ]
                });
            }
            if (!text) return '—';
            return /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex flex-wrap items-center gap-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-ui",
                        children: text
                    }),
                    home.canEdit ? /*#__PURE__*/ _jsx(NoticeAdd, {
                        noticeId: n.noticeId,
                        observation: obs,
                        rfiId: rfiId,
                        role: role
                    }) : null
                ]
            });
        };
        sections.push({
            id: 'notice',
            title: 'Notice',
            facts: [
                {
                    id: 'id',
                    label: 'Notice ID',
                    value: n.noticeId
                },
                {
                    id: 'type',
                    label: 'Type',
                    value: n.typeLabel || '—'
                },
                {
                    id: 'office',
                    label: 'Office',
                    value: ref(n.officeRef, n.officeRefSource, n.officeName, 'issuing_office', {
                        kind: 'GOVERNMENT_ORG',
                        name: n.officeName,
                        orgCode: n.officeCode
                    })
                },
                {
                    id: 'vehicle',
                    label: 'Vehicle',
                    value: ref(n.vehicleRef, n.vehicleRefSource, n.vehicleLabel, 'vehicle', {
                        kind: 'VEHICLE',
                        name: n.vehicleLabel,
                        contractNumber: n.vehicleContractNumber
                    })
                },
                {
                    id: 'posted',
                    label: 'Posted',
                    value: n.postedAt ? format(n.postedAt.toDate(), 'd MMM yyyy') : '—'
                },
                {
                    id: 'poc',
                    label: 'Point of contact',
                    value: ref(n.pointOfContactRef, n.pointOfContactRefSource, n.pointOfContactName, 'point_of_contact', {
                        kind: 'CONTACT',
                        name: n.pointOfContactName,
                        email: n.pointOfContactEmail
                    })
                }
            ]
        });
    }
    const due = home.responseDue?.toDate();
    const qDue = home.questionsDue?.toDate();
    const fmt = home.submissionFormat;
    const formatValue = (fmt || '—') + (home.kpis?.pageLimit && !/page/i.test(fmt) ? `, ${home.kpis.pageLimit} pages max` : '');
    const sendTo = byRole('submission_recipient');
    const questionsTo = byRole('questions_to');
    sections.push({
        id: 'submission',
        title: 'Submission',
        action: home.canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
            "data-testid": T.editSubmissionFacts,
            onClick: ()=>setEditing(true),
            size: "inline",
            variant: "link",
            children: "Edit"
        }) : undefined,
        facts: [
            {
                id: 'due',
                label: 'Due',
                value: due ? format(due, 'd MMM, h:mm a') : '—'
            },
            {
                id: 'format',
                label: 'Format',
                value: formatValue
            },
            {
                id: 'send-to',
                label: 'Send to',
                value: sendTo.length === 0 ? '—' : /*#__PURE__*/ _jsx("span", {
                    className: "inline-flex flex-wrap gap-[var(--space-1-5)]",
                    children: sendTo.map((e)=>/*#__PURE__*/ _jsx("span", {
                            children: entityChip(e)
                        }, e.linkId))
                })
            },
            {
                id: 'questions-to',
                label: `Questions to ${home.agencyShort || 'the agency'}`,
                value: /*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex flex-wrap items-center gap-[var(--space-1-5)]",
                    children: [
                        qDue ? `${qDue < new Date() ? 'closed' : 'due'} ${format(qDue, 'd MMM')}` : '—',
                        questionsTo.map((e)=>/*#__PURE__*/ _jsx("span", {
                                children: entityChip(e)
                            }, e.linkId))
                    ]
                })
            }
        ]
    });
    sections.push({
        id: 'people',
        title: 'People and organizations',
        action: home.canEdit ? /*#__PURE__*/ _jsx(RfiEntityAddAction, {
            rfiResponseProjectId: rfiId
        }) : undefined,
        children: /*#__PURE__*/ _jsx(RfiEntitiesRailGroup, {
            canEdit: home.canEdit,
            entities: home.entities,
            rfiResponseProjectId: rfiId
        })
    });
    if (home.linkedPursuits.length > 0) {
        sections.push({
            id: 'pursuits',
            title: 'Linked pursuits',
            rows: home.linkedPursuits.map((p)=>({
                    id: p.captureProjectId,
                    label: /*#__PURE__*/ _jsx(SpecraLink, {
                        className: "font-medium",
                        href: `/capture/${p.captureProjectId}`,
                        renderLink: renderRouterLink,
                        children: p.title
                    }),
                    value: p.primary ? 'primary' : p.questionNumbers.length > 0 ? `question${p.questionNumbers.length > 1 ? 's' : ''} ${p.questionNumbers.join(' and ')}` : undefined
                }))
        });
    }
    const owned = new Map();
    for (const q of home.questions){
        if (q.ownerUserId) owned.set(q.ownerUserId, (owned.get(q.ownerUserId) ?? 0) + 1);
    }
    if (zouProjectId) {
        sections.push({
            id: 'team',
            title: 'Team',
            children: /*#__PURE__*/ _jsx(TeamRows, {
                owned: owned,
                zouProjectId: zouProjectId
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraRecordRail, {
                "data-testid": T.rail,
                sections: sections
            }),
            editing ? /*#__PURE__*/ _jsx(RfiSubmissionFactsDialog, {
                facts: {
                    submissionFormat: home.submissionFormat,
                    questionsDue: qDue,
                    pageLimit: home.kpis?.pageLimit ?? 0,
                    wordLimit: home.wordLimit,
                    wordsPerPage: home.wordsPerPage || 500
                },
                onOpenChange: setEditing,
                open: editing,
                rfiResponseProjectId: rfiId
            }) : null
        ]
    });
}
